import { countBy, sumBy, uniq } from "lodash-es"
import {
  Building2,
  CircleAlert,
  Clock,
  type LucideIcon,
  MapPinOff,
  Unplug,
} from "lucide-react"
import { z } from "zod"

import type { CompanyRunResearch } from "@/admin/monitoring/company-run-research-data"
import {
  bandSummary,
  isLiveCell,
  isOpenCell,
  type ResearchBand,
  ResearchCategoryRail,
  toneClassName,
} from "@/admin/monitoring/research-category-rail"
import { dataByResearchMark } from "@/admin/monitoring/research-mark"
import {
  ResearchMarkSquare,
  ResearchSourceTable,
  ShareBar,
  sourceDescription,
} from "@/admin/monitoring/research-source-table"
import {
  formatSearchGeography,
  humanizeKey,
} from "@/admin/monitoring/run-presentation"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ExecutionOutcome } from "@/constants/terminal/execution-outcome"
import {
  dataByResearchCategory,
  researchCategories,
  ResearchCategory,
} from "@/constants/terminal/research-category"
import { ResearchSufficiencyOutcome } from "@/constants/terminal/research-sufficiency-outcome"
import {
  dataBySearchSubjectKind,
  SearchSubjectKind,
} from "@/constants/terminal/search-subject-kind"
import { cn } from "@/lib/utils"
import { usd } from "@/terminal/monitoring/usage-presentation"
import { sourcePlatformDisplayName } from "@/terminal/research/platform-display-names"

const ResearchCategorySchema = z.nativeEnum(ResearchCategory)

type Coverage = NonNullable<ResearchBand["coverage"]>
type Cell = ResearchBand["cells"][number]

// Requirements the Research Tasks table can't show: uncovered and uncaptured
// ones are already its rows, counted by its outcome filter.
const gapGroups = [
  {
    key: "noLegalEntityRequirements",
    icon: Building2,
    title: "No Legal Entity in Company Scope",
    description:
      "These search for a Legal Entity, and Company Scope holds none.",
  },
  {
    key: "formationJurisdictionUnsupportedRequirements",
    icon: MapPinOff,
    title: "Formation jurisdiction unknown",
    description:
      "No one knows where the Legal Entity was formed, and the registry that could tell isn’t supported in any Operating Jurisdiction.",
  },
  {
    key: "unreachableRequirements",
    icon: Unplug,
    title: "Website or domain unreachable",
    description:
      "The website or RDAP check ran and was blocked or failed. A Company needn’t have a working website.",
  },
  {
    key: "deferredRequirements",
    icon: Clock,
    title: "Deferred to a later Run",
    description:
      "Optional searches past the per-Run budget. Disclosed on the File; never blocks it.",
  },
  {
    key: "incompleteAdditionalRequirements",
    icon: CircleAlert,
    title: "Additional search incomplete",
    description:
      "Optional searches that ran and ended blocked, failed, partial or at their bound. Disclosed on the File; never blocks it.",
  },
] as const satisfies readonly {
  key: keyof Coverage
  icon: LucideIcon
  title: string
  description: string
}[]

const CategoryLabel = ({
  category,
  band,
}: {
  category: ResearchCategory
  band: ResearchBand | undefined
}) => (
  <>
    {dataByResearchCategory[category].name}{" "}
    <span className="text-muted-foreground">{bandSummary(band).label}</span>
  </>
)

// Checked out of total as the Records column's bar, so progress reads at a
// glance and the numbers stay beside it.
const Meter = ({
  label,
  checked,
  total,
}: {
  label: string
  checked: number
  total: number
}) => (
  <span className="flex items-center gap-2">
    <span className="text-muted-foreground">{label}</span>
    {total === 0 ? (
      "None"
    ) : (
      <>
        <ShareBar share={checked / total} fillClassName="bg-good-foreground" />
        <span className="tabular-nums">
          {checked}/{total}
        </span>
      </>
    )}
  </span>
)

// The counts the selected category's verdict rests on, so the Research Tasks
// table below never has to repeat them.
const CategoryHeader = ({
  category,
  band,
  subjectNameByKey,
}: {
  category: ResearchCategory
  band: ResearchBand | undefined
  subjectNameByKey: Record<string, string>
}) => {
  const summary = bandSummary(band)
  const cells = band?.cells ?? []
  const spentUsd = sumBy(cells, (cell) => cell.spendUsd ?? 0)
  const subjectNames = uniq(
    cells.map((cell) => subjectNameByKey[cell.subjectKey] ?? cell.subjectKey)
  )
  // Two admitted listings on one platform may be one business twice.
  const listingCountByPlatform = countBy(
    cells.filter((cell) => cell.sourceKey.includes(":")),
    "platformKey"
  )
  const repeatedPlatforms = Object.entries(listingCountByPlatform).filter(
    ([, listingCount]) => listingCount > 1
  )
  return (
    <header className="space-y-3">
      {/* The rail (or the select below lg) already names the category and
          its verdict. */}
      <h2 className="sr-only">{dataByResearchCategory[category].name}</h2>
      {summary.detail && (
        <p className={cn("text-sm", toneClassName[summary.tone])}>
          {summary.detail}
        </p>
      )}
      {cells.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
          {band?.isReportOnly ? (
            <Meter
              label="Checked"
              checked={summary.optionalCheckedCount}
              total={summary.optionalCount}
            />
          ) : (
            <>
              <Meter
                label="Required"
                checked={summary.checkedCount}
                total={summary.weighedCount}
              />
              {summary.optionalCount > 0 && (
                <Meter
                  label="Optional"
                  checked={summary.optionalCheckedCount}
                  total={summary.optionalCount}
                />
              )}
            </>
          )}
          <span
            className="text-muted-foreground"
            title={subjectNames.join("\n")}
          >
            {subjectNames.length === 1
              ? subjectNames[0]
              : `${subjectNames.length} Search Subjects`}
          </span>
          {spentUsd > 0 && (
            <span className="text-muted-foreground tabular-nums">
              {usd(spentUsd)} spent
            </span>
          )}
        </div>
      )}
      {repeatedPlatforms.map(([platformKey, listingCount]) => (
        <p key={platformKey} className="text-caution-foreground text-xs">
          {listingCount} {sourcePlatformDisplayName(platformKey)} listings were
          admitted. Check that they aren’t the same business twice.
        </p>
      ))}
    </header>
  )
}

// Every search across categories that has no usable answer, blocking ones
// first, so the first screen says what is wrong and where to look. Report-only
// gaps follow: disclosed on the File, never blocking it.
const ResearchAttention = ({
  bands,
  onOpenTask,
  onSelectCategory,
}: {
  bands: ResearchBand[]
  onOpenTask: (id: string) => void
  onSelectCategory: (category: ResearchCategory) => void
}) => {
  const categorized = bands.flatMap((band) =>
    band.category === null
      ? []
      : band.cells.map((cell) => ({ band, category: band.category!, cell }))
  )
  const blocking = categorized.filter(
    ({ band, cell }) => !band.isReportOnly && cell.isWeighed && isOpenCell(cell)
  )
  const disclosed = categorized.filter(
    ({ band, cell }) => band.isReportOnly && isOpenCell(cell)
  )
  const liveCount = categorized.filter(({ cell }) => isLiveCell(cell)).length
  const categoryBands = bands.filter((band) => band.category !== null)
  const insufficientCount = categoryBands.filter(
    (band) => band.outcome === ResearchSufficiencyOutcome.INSUFFICIENT
  ).length
  const items = [...blocking, ...disclosed]
  return (
    <section
      aria-label="Research Tasks needing attention"
      className="space-y-3"
    >
      <p className="text-sm">
        {insufficientCount > 0 ? (
          <span className={cn("font-medium", toneClassName.bad)}>
            {insufficientCount} of {categoryBands.length} Research Categories
            insufficient
          </span>
        ) : (
          `All ${categoryBands.length} Research Categories sufficient`
        )}
        <span className="text-muted-foreground">
          {blocking.length > 0 &&
            `; ${blocking.length} required Research ${blocking.length === 1 ? "Task has" : "Tasks have"} no usable Source Check`}
          {liveCount > 0 && `; ${liveCount} Research Tasks running`}.
        </span>
      </p>
      {items.length > 0 && (
        <ul className="divide-border-faint divide-y border-y text-xs">
          {items.map(({ band, category, cell }, index) => (
            <AttentionItem
              key={`${cell.researchTaskId ?? cell.sourceKey}:${index}`}
              cell={cell}
              categoryName={dataByResearchCategory[category].name}
              isDisclosure={band.isReportOnly}
              onOpenTask={onOpenTask}
              onShowCategory={() => onSelectCategory(category)}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

const AttentionItem = ({
  cell,
  categoryName,
  isDisclosure,
  onOpenTask,
  onShowCategory,
}: {
  cell: Cell
  categoryName: string
  isDisclosure: boolean
  onOpenTask: (id: string) => void
  onShowCategory: () => void
}) => {
  const { researchTaskId } = cell
  return (
    <li className="grid items-center gap-x-4 gap-y-1 py-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_8rem]">
      <span className="flex items-center gap-2.5">
        <ResearchMarkSquare mark={cell.mark} />
        <span>
          {sourcePlatformDisplayName(cell.platformKey)}
          <span className="text-muted-foreground block">
            {sourceDescription(cell)}
          </span>
        </span>
      </span>
      <span>
        {cell.extractionOutcome === ExecutionOutcome.FAILED
          ? "Extraction failed"
          : dataByResearchMark[cell.mark].label}
        <span className="text-muted-foreground block">
          <button
            className="hover:text-foreground underline underline-offset-2"
            onClick={onShowCategory}
          >
            {categoryName}
          </button>
          {isDisclosure ? ", report only" : ", required"}
        </span>
      </span>
      {researchTaskId ? (
        <button
          className="justify-self-start underline-offset-2 hover:underline sm:justify-self-end"
          data-research-task-id={researchTaskId}
          onClick={() => onOpenTask(researchTaskId)}
        >
          Open Source Check
        </button>
      ) : (
        <span />
      )}
    </li>
  )
}

export const CompanyRunResearchView = ({
  research,
  onOpenTask,
  category: requestedCategory,
  onSelectCategory,
}: {
  research: CompanyRunResearch
  onOpenTask: (id: string) => void
  category?: ResearchCategory
  onSelectCategory: (category: ResearchCategory) => void
}) => {
  const bandByCategory: Partial<Record<ResearchCategory, ResearchBand>> = {}
  for (const band of research.bands)
    if (band.category) bandByCategory[band.category] = band
  const selectedCategory =
    requestedCategory ??
    researchCategories.find(
      (category) =>
        bandByCategory[category]?.outcome ===
        ResearchSufficiencyOutcome.INSUFFICIENT
    ) ??
    researchCategories[0]
  const selectedBand = bandByCategory[selectedCategory]
  const additionalCells = research.bands.flatMap((band) =>
    band.category === null ? band.cells : []
  )
  return (
    <section className="space-y-8">
      <ResearchAttention
        bands={research.bands}
        onOpenTask={onOpenTask}
        onSelectCategory={onSelectCategory}
      />
      {research.isSufficiencyProvisional && (
        <p className="text-muted-foreground text-sm">
          Provisional: Research Sufficiency isn’t frozen yet.
        </p>
      )}
      <div className="grid gap-8 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <ResearchCategoryRail
          bandByCategory={bandByCategory}
          selectedCategory={selectedCategory}
          onSelectCategory={onSelectCategory}
        />
        <div className="min-w-0 space-y-6">
          <Select
            value={selectedCategory}
            onValueChange={(value) =>
              onSelectCategory(ResearchCategorySchema.parse(value))
            }
          >
            <SelectTrigger
              aria-label="Research category"
              className="h-9 w-full rounded-sm text-sm lg:hidden"
            >
              <SelectValue>
                <CategoryLabel
                  category={selectedCategory}
                  band={selectedBand}
                />
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {researchCategories.map((category) => (
                <SelectItem key={category} value={category}>
                  <CategoryLabel
                    category={category}
                    band={bandByCategory[category]}
                  />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CategoryHeader
            key={selectedCategory}
            category={selectedCategory}
            band={selectedBand}
            subjectNameByKey={research.subjectNameByKey}
          />
          {selectedBand?.coverage && (
            <CoverageGaps
              coverage={selectedBand.coverage}
              subjectNameByKey={research.subjectNameByKey}
            />
          )}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Research Tasks</h3>
            {selectedBand?.cells.length ? (
              <ResearchSourceTable
                key={selectedCategory}
                research={research}
                cells={selectedBand.cells}
                onOpenTask={onOpenTask}
              />
            ) : (
              <p className="text-muted-foreground text-sm">
                No Research Tasks were recorded for this category.
              </p>
            )}
          </section>
        </div>
      </div>
      {additionalCells.length > 0 && (
        <details className="space-y-3 border-t pt-6">
          <summary className="text-sm font-semibold">
            Other Research Tasks
          </summary>
          <p className="text-muted-foreground mt-2 mb-3 text-xs">
            No displayed Research Category requires these.
          </p>
          <ResearchSourceTable
            research={research}
            cells={additionalCells}
            onOpenTask={onOpenTask}
          />
        </details>
      )}
    </section>
  )
}

// The selected category's unmet requirements the table can't show, one
// compact group per kind of gap; each group's meaning sits in its tooltip.
const CoverageGaps = ({
  coverage,
  subjectNameByKey,
}: {
  coverage: Coverage
  subjectNameByKey: Record<string, string>
}) =>
  gapGroups.map(({ key, icon: Icon, title, description }) => {
    const requirements = coverage[key] ?? []
    return (
      requirements.length > 0 && (
        <section key={key} className="space-y-1.5">
          <h3
            className="flex items-center gap-2 text-sm font-semibold"
            title={description}
          >
            <Icon className="text-muted-foreground size-3.5" />
            {title}
            <span className="text-muted-foreground font-normal tabular-nums">
              {requirements.length}
            </span>
          </h3>
          <RequirementList
            subjectNameByKey={subjectNameByKey}
            requirements={requirements}
          />
        </section>
      )
    )
  })

const RequirementList = ({
  subjectNameByKey,
  requirements,
}: {
  subjectNameByKey: Record<string, string>
  requirements: NonNullable<Coverage["deferredRequirements"]>
}) => (
  <ul className="divide-border-faint divide-y border-y text-xs">
    {requirements.map((requirement, index) => {
      const subject = requirement.searchSubject
      const subjectId =
        subject.kind === SearchSubjectKind.BRAND
          ? subject.brandId
          : subject.legalEntityId
      const subjectLabel = dataBySearchSubjectKind[subject.kind].label
      const subjectName = subjectId
        ? (subjectNameByKey[`${subject.kind}:${subjectId}`] ??
          `${subjectLabel} ${subjectId.slice(0, 8)}`)
        : `No ${subjectLabel} recorded`
      return (
        <li
          key={index}
          className="grid gap-x-4 py-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_5rem]"
        >
          <span className="truncate">
            {sourcePlatformDisplayName(requirement.platformKey)}{" "}
            <span className="text-muted-foreground">
              {humanizeKey(requirement.sourceKey).toLowerCase()}
            </span>
          </span>
          <span className="truncate">{subjectName}</span>
          <span className="text-muted-foreground tabular-nums">
            {formatSearchGeography(requirement.searchGeography)}
          </span>
        </li>
      )
    })}
  </ul>
)
